import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import GeoPhotoLightbox from "./GeoPhotoLightbox";
import { loomPhotoItems } from "./loomPhotos";
import { parsePublicLoomCode } from "./loomQr";
import { getPublicLoom } from "../../services/geoLoomApi";
import { unwrapGeoPayload } from "../gi-registry/geoPath";
import "../gi-registry/gi-registry.scss";
import "./public-loom.scss";

const formatAddedOn = (value: any) => {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return new Intl.DateTimeFormat("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
};

const PublicLoomPage = () => {
  const location = useLocation();
  const loomCode = parsePublicLoomCode(location.pathname);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [record, setRecord] = useState<any>(null);
  const [lightboxIndex, setLightboxIndex] = useState(-1);

  useEffect(() => {
    if (!loomCode) {
      setError("This loom tag could not be read.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    getPublicLoom(loomCode)
      .then((res: any) => {
        const view = unwrapGeoPayload(res);
        if (!res?.success || !view?.loomCode) {
          setRecord(null);
          setError(res?.message === "FEATURE_DISABLED"
            ? "This loom page is not available yet."
            : "This loom tag is not on record.");
          return;
        }
        setRecord(view);
      })
      .catch(() => {
        setRecord(null);
        setError("This loom tag could not be verified right now.");
      })
      .finally(() => setLoading(false));
  }, [loomCode]);

  const photos = loomPhotoItems(record);
  const addedOn = formatAddedOn(record?.taggedAt);

  return (
    <div className="public-loom-page">
      <header className="public-loom-page__top">
        <Link to="/home" className="public-loom-page__brand">
          HastKalaPramanak
        </Link>
        <span className="public-loom-page__eyebrow">Geotagged loom</span>
      </header>
      <main className="public-loom-page__main">
        {loading ? (
          <p className="public-loom-page__status">Checking this loom tag…</p>
        ) : error ? (
          <div className="public-loom-page__status">
            <h1>Loom not found</h1>
            <p>{error}</p>
          </div>
        ) : (
          <>
            <p className="public-loom-page__kicker">Loom ID</p>
            <h1>{record.loomCode}</h1>
            {record.active === false ? (
              <p className="public-loom-page__inactive">This loom is marked inactive.</p>
            ) : null}
            <p className="public-loom-page__gi">{record.giName || "Geographical Indication"}</p>
            {record.giState ? (
              <p className="public-loom-page__meta">{record.giState}</p>
            ) : null}
            {record.orgName || addedOn ? (
              <dl className="public-loom-page__facts">
                {record.orgName ? (
                  <>
                    <dt>Organisation</dt>
                    <dd>{record.orgName}</dd>
                  </>
                ) : null}
                {addedOn ? (
                  <>
                    <dt>Added</dt>
                    <dd>{addedOn}</dd>
                  </>
                ) : null}
              </dl>
            ) : null}
            <span className={record.insideGi ? "gi-status-chip is-frozen" : "gi-status-chip is-rejected"}>
              {record.distanceLabel || (record.insideGi ? "Inside the GI fence" : "Outside the GI fence")}
            </span>
            {photos.length > 0 ? (
              <div className="public-loom-page__photos">
                {photos.map((photo, idx) => (
                  <button
                    type="button"
                    key={`${photo.url}-${idx}`}
                    className="public-loom-page__thumb"
                    onClick={() => setLightboxIndex(idx)}
                  >
                    <img src={photo.url} alt={photo.label} />
                    <span>{photo.label}</span>
                  </button>
                ))}
              </div>
            ) : (
              <p className="public-loom-page__meta">No photos are attached to this loom yet.</p>
            )}
            <p className="public-loom-page__note">
              This page confirms the loom was geotagged against a registered GI production area.
              It is not a product certificate.
            </p>
          </>
        )}
      </main>
      <GeoPhotoLightbox
        items={photos}
        index={lightboxIndex}
        onClose={() => setLightboxIndex(-1)}
        onIndex={setLightboxIndex}
      />
    </div>
  );
};

export default PublicLoomPage;
